import { useState } from 'react';

export default function HelpPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<'all' | 'quotations' | 'parties' | 'import' | 'reports' | 'faq'>('all');

  const categories = [
    { id: 'all', label: 'All Topics', icon: '📚' },
    { id: 'quotations', label: 'Quotations', icon: '📄' },
    { id: 'parties', label: 'Customer Parties', icon: '👥' },
    { id: 'import', label: 'Bulk Imports', icon: '📥' },
    { id: 'reports', label: 'Analytics & Reports', icon: '📈' },
    { id: 'faq', label: 'FAQs & Troubleshooting', icon: '❓' },
  ];

  const guideSections = [
    {
      id: 'quote-create',
      category: 'quotations',
      title: 'Creating and Calculating a Quotation',
      description: 'How to issue a new commercial quotation with real-time VBA pricing math and dual exports.',
      steps: [
        'Navigate to the Quotations page from the left sidebar and click the blue "+ New Quotation" button.',
        'Select the issuing Company Branch (e.g. Sai Krishna Industries, Sutran Polymers GGN, Sutran Conveyors KOL). This determines the quotation prefix, HSN codes, and bank remittance info.',
        'Select the quoting Sales Representative (AB, SB, JP, VG, RR, DK, AK). Their initials form the quotation reference code.',
        'Choose the Customer Party from the 664 master database. The registered factory address, contact person, and GSTIN populate automatically.',
        'Add Line Items: Enter the assembly item code (e.g. SF1/SW/PP/F/615), quantity, and base rate. There is no line limit (unlike the old 5-item Excel macro ceiling).',
        'Configure Commercial Terms: Set Packaging & Forwarding (P&F) percentage (strictly calculated on Subtotal), Freight terms (EX-WORKS), and payment conditions.',
        'Verify the financial summary: Subtotal, P&F, GST (18%), and Total calculate automatically with integer ceiling RoundUp math.',
        'Click "Save Quotation". You can now download the official print-ready PDF or synchronized Excel workbook.',
      ],
    },
    {
      id: 'quote-export',
      category: 'quotations',
      title: 'Exporting to PDF and Excel (.xlsx)',
      description: 'Understanding the output formats and layout specifications.',
      steps: [
        'PDF Export (PDF 📄): Generates an authentic A4 PDF document with company legal header, 5mm vertical spacing after HSN codes, non-overlapping financial columns, banker remittance details, authorised signatory, and Amount in Words.',
        'Excel Export (Excel 📊): Downloads a format-synchronized Microsoft Excel (.xlsx) file matching the legacy Preview sheet for spreadsheet archiving.',
        'Both formats run directly inside the browser using client-side JavaScript—working seamlessly on Windows, Mac, iPad, and Android.',
      ],
    },
    {
      id: 'parties-mgmt',
      category: 'parties',
      title: 'Managing the Customer Parties Database',
      description: 'How to search, add, edit, and delete customer accounts.',
      steps: [
        'Navigate to Customer Parties to browse the 664 deduplicated customer master accounts.',
        'Search instantly by party name, contact person, city, phone number, or 15-character GSTIN.',
        'Add a new customer by clicking "+ Add Party" and entering the business details.',
        'To edit existing information (such as updated phone numbers or GST numbers), click the "Edit ✏️" button on the customer row.',
        'To remove an obsolete customer, click "Delete 🗑️" and confirm the safety prompt.',
      ],
    },
    {
      id: 'import-guide',
      category: 'import',
      title: 'Bulk Excel Data Import with Official Templates',
      description: 'How to upload customer batches, product lists, and price updates.',
      steps: [
        'Navigate to Bulk Import from the left sidebar.',
        'Select the target data type: Parties, Products, Price List, Companies, or Sales Reps.',
        'Click "📥 Download Template (.xlsx)" to download the pre-formatted Excel template with navy headers and pre-filled sample rows.',
        'Open the file in Microsoft Excel. Paste or type your data rows without modifying the column headers.',
        'Save the file and upload it by dragging and dropping it into the upload dropzone.',
        'The portal validates each row and displays a green summary of inserted records. Any invalid rows will be highlighted with exact row numbers.',
      ],
    },
    {
      id: 'reports-guide',
      category: 'reports',
      title: 'Executive Financial Reports & Analytics',
      description: 'Monitoring company revenue, rep performance, and downloading CSV reports.',
      steps: [
        'Navigate to Reports & Analytics from the left sidebar.',
        'Review the executive KPI cards: Gross Subtotal, Total Discounts Conceded, Active Pipeline, Approved Revenue, and Tax Liability.',
        'Inspect the Branch Distribution chart to compare volume between SKI, TCI, SPPL, and SCPL.',
        'Analyze the Sales Rep rankings table to track win rates and quotation volumes.',
        'Click the green "Export CSV Report" button at the top right to download raw data for spreadsheet auditing.',
      ],
    },
    {
      id: 'troubleshooting-faq',
      category: 'faq',
      title: 'Frequently Asked Questions & Troubleshooting',
      description: 'Common solutions, system requirements, and support escalations.',
      steps: [
        'Q: Can I create quotes with more than 5 line items? Yes! The web app supports unlimited line items per quotation.',
        'Q: How do I know if the math matches the old Excel macro? Go to the Project Tracker page and click "⚡ Run VBA Math Test". It tests against factory benchmarks with 100% precision.',
        'Q: How do I update a component price? Go to Price List, locate the item, and edit the price, or upload a bulk price list via the Import page.',
        'Q: Need Technical Support? Contact: sutrantechnoconveyors@gmail.com | Phone: 0161-5034059, 93167-53530, 98887-53533.',
      ],
    },
  ];

  const filteredSections = guideSections.filter((sec) => {
    const matchesCategory = activeCategory === 'all' || sec.category === activeCategory;
    const matchesQuery =
      searchQuery === '' ||
      sec.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sec.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sec.steps.some((step) => step.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesQuery;
  });

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-blue-900 rounded-2xl shadow-elevated text-white p-6 sm:p-8 border border-slate-700/50">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 text-xs font-bold uppercase tracking-wider mb-3">
            <span>📖</span> Knowledge Base & SOP Manual
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
            How Can We Help You Today?
          </h1>
          <p className="text-slate-300 text-sm mt-2 leading-relaxed">
            Welcome to the official Sai Krishna Industries portal guide. Find step-by-step instructions, workflow procedures, data formatting rules, and technical assistance.
          </p>

          {/* Search Box */}
          <div className="mt-6 relative">
            <input
              type="text"
              placeholder="Search help topics, error messages, workflows, or terms..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 pl-11 rounded-xl bg-white/10 border border-white/20 text-white placeholder-slate-400 focus:bg-white focus:text-slate-900 focus:border-blue-500 text-sm shadow-inner transition-all"
            />
            <span className="absolute left-4 top-3.5 text-slate-400 text-base">🔍</span>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-3 text-xs bg-white/20 hover:bg-white/30 text-white px-2 py-1 rounded font-semibold"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Category Pills */}
      <div className="flex flex-wrap gap-2 pb-2 border-b border-slate-200">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id as any)}
            className={`px-3.5 py-2 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all ${
              activeCategory === cat.id
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-white text-slate-600 hover:text-slate-900 hover:bg-slate-100 border border-slate-200'
            }`}
          >
            <span>{cat.icon}</span>
            <span>{cat.label}</span>
          </button>
        ))}
      </div>

      {/* Guide Cards */}
      <div className="space-y-4">
        {filteredSections.length === 0 ? (
          <div className="p-12 text-center bg-white rounded-xl border border-slate-200 text-slate-500">
            <span className="text-3xl">🔍</span>
            <p className="font-bold text-slate-800 text-base mt-2">No matching help topics found</p>
            <p className="text-xs mt-1 text-slate-500">Try adjusting your search keywords or clear the search filter.</p>
          </div>
        ) : (
          filteredSections.map((sec) => (
            <div
              key={sec.id}
              className="bg-white rounded-xl shadow-card border border-slate-200/80 p-6 hover:border-slate-300 transition-all space-y-4"
            >
              <div>
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-blue-600">
                  <span>{categories.find((c) => c.id === sec.category)?.icon}</span>
                  <span>{categories.find((c) => c.id === sec.category)?.label}</span>
                </div>
                <h2 className="text-lg font-bold text-slate-900 mt-1">{sec.title}</h2>
                <p className="text-xs text-slate-500 mt-0.5">{sec.description}</p>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200/60">
                <h3 className="text-xs font-extrabold uppercase tracking-wider text-slate-700 mb-2">
                  Step-by-Step Instructions:
                </h3>
                <ol className="space-y-2 text-xs text-slate-700">
                  {sec.steps.map((step, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="flex-shrink-0 h-5 w-5 rounded-full bg-blue-100 text-blue-800 font-bold text-[10px] flex items-center justify-center mt-0.5">
                        {idx + 1}
                      </span>
                      <span className="leading-relaxed">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Support Contact Footer */}
      <div className="bg-slate-100 rounded-xl p-6 border border-slate-300 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <span>📞</span> Need Additional Assistance?
          </h3>
          <p className="text-xs text-slate-600 mt-0.5">
            Contact the Sai Krishna Industries IT Support & Systems Team
          </p>
        </div>
        <div className="text-xs text-slate-700 space-y-1">
          <p><strong>Email:</strong> <a href="mailto:sutrantechnoconveyors@gmail.com" className="text-blue-600 underline">sutrantechnoconveyors@gmail.com</a></p>
          <p><strong>Head Office:</strong> 0161-5034059, 93167-53530, 98887-53533</p>
        </div>
      </div>
    </div>
  );
}
